import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Stack, Tabs } from '@mantine/core'
import { useSearchParams } from 'react-router-dom'
import { PageShell } from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api } from '@/lib/api'
import {
  buildDashboardPeriodParams,
  type DashboardPeriodKey,
} from '@/lib/dashboard-period'
import {
  defaultPermissionsForRole,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import type { MyPermissionsResponse, ProjectRow } from '@/types/api'
import { AnalyticsPeriodFilters } from '@/pages/analytics/AnalyticsPeriodFilters'
const AnalyticsOverviewTab = lazy(async () => import('@/pages/analytics/AnalyticsOverviewTab').then((m) => ({ default: m.AnalyticsOverviewTab })))
const AnalyticsOperatorsTab = lazy(async () => import('@/pages/analytics/AnalyticsOperatorsTab').then((m) => ({ default: m.AnalyticsOperatorsTab })))
const AnalyticsReviewsTab = lazy(async () => import('@/pages/analytics/AnalyticsReviewsTab').then((m) => ({ default: m.AnalyticsReviewsTab })))
const AnalyticsSlowTicketsTab = lazy(async () => import('@/pages/analytics/AnalyticsSlowTicketsTab').then((m) => ({ default: m.AnalyticsSlowTicketsTab })))
const AnalyticsTagsTab = lazy(async () => import('@/pages/analytics/AnalyticsTagsTab').then((m) => ({ default: m.AnalyticsTagsTab })))
const AnalyticsAiOpsTab = lazy(async () => import('@/pages/analytics/AnalyticsAiOpsTab').then((m) => ({ default: m.AnalyticsAiOpsTab })))

const ANALYTICS_TABS = ['overview', 'reviews', 'operators', 'tags', 'ai-ops', 'slow'] as const
type AnalyticsTab = (typeof ANALYTICS_TABS)[number]

function isAnalyticsTab(value: string | null): value is AnalyticsTab {
  return !!value && (ANALYTICS_TABS as readonly string[]).includes(value)
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function toLocalDatetimeValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

export function AnalyticsPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])
  const p = perm ?? defaultPermissionsForRole(user?.role)
  const canAnalytics = hasPermission(p, 'access_analytics')
  const canDebug = hasPermission(p, 'access_debug')

  const [period, setPeriod] = useState<DashboardPeriodKey>('7')
  const [projectIds, setProjectIds] = useState<string[]>([])
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [customFrom, setCustomFrom] = useState(() => {
    const t = new Date()
    const from = new Date(t.getTime() - 30 * 24 * 60 * 60 * 1000)
    return toLocalDatetimeValue(from)
  })
  const [customTo, setCustomTo] = useState(() => toLocalDatetimeValue(new Date()))
  const [customApplied, setCustomApplied] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const rawTab = searchParams.get('tab')
  const tab: AnalyticsTab = isAnalyticsTab(rawTab) && (rawTab !== 'slow' || canDebug) ? rawTab : 'overview'

  useEffect(() => {
    void api.projects().then((pl) =>
      setProjects([...pl].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))),
    )
  }, [])

  const selectedProjectId = projectIds[0] || ''
  const customPeriodPending = period === 'custom_datetime' && !customApplied
  const handlePeriodChange = useCallback((next: DashboardPeriodKey) => {
    setPeriod(next)
    setCustomApplied(next !== 'custom_datetime')
  }, [])
  const handleCustomApply = useCallback(() => setCustomApplied(true), [])

  const periodParams = useMemo(() => {
    let customFromIso: string | null = null
    let customToIso: string | null = null
    if (period === 'custom_datetime' && customApplied && customFrom && customTo) {
      const df = new Date(customFrom)
      const dt = new Date(customTo)
      if (!Number.isNaN(df.getTime())) customFromIso = df.toISOString()
      if (!Number.isNaN(dt.getTime())) customToIso = dt.toISOString()
    }
    return buildDashboardPeriodParams(period, {
      projectId: selectedProjectId || undefined,
      customFromIso,
      customToIso,
      excludeBurst: false,
    })
  }, [period, selectedProjectId, customFrom, customTo, customApplied])

  const customRangeError = useMemo(() => {
    if (period !== 'custom_datetime' || !customApplied) return null
    if (!customFrom || !customTo) return 'Укажите даты «От» и «До».'
    const df = new Date(customFrom)
    const dt = new Date(customTo)
    if (Number.isNaN(df.getTime()) || Number.isNaN(dt.getTime())) return 'Некорректная дата в своём периоде.'
    if (dt <= df) return 'Дата «До» должна быть позже даты «От».'
    return null
  }, [period, customApplied, customFrom, customTo])

  const periodKey = useMemo(() => JSON.stringify(periodParams), [periodParams])

  const setTab = useCallback(
    (value: string | null) => {
      const nextTab = isAnalyticsTab(value) && (value !== 'slow' || canDebug) ? value : 'overview'
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (nextTab === 'overview') next.delete('tab')
          else next.set('tab', nextTab)
          return next
        },
        { replace: true },
      )
    },
    [canDebug, setSearchParams],
  )

  const onRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1)
  }, [])

  if (!canAnalytics) {
    return (
      <PageShell>
        <Alert color="gray" title="Нет доступа">
          Недостаточно прав для раздела «Аналитика».
        </Alert>
      </PageShell>
    )
  }

  const periodFilters = (
    <AnalyticsPeriodFilters
      period={period}
      onPeriodChange={handlePeriodChange}
      projectIds={projectIds}
      onProjectIdsChange={setProjectIds}
      projects={projects}
      customFrom={customFrom}
      customTo={customTo}
      onCustomFromChange={setCustomFrom}
      onCustomToChange={setCustomTo}
      customApplied={customApplied}
      onCustomApply={handleCustomApply}
      onRefresh={customPeriodPending ? () => {} : onRefresh}
      loading={false}
    />
  )

  if (customPeriodPending) {
    return (
      <PageShell>
        <Stack gap="md">
          {periodFilters}
          <Alert color="blue" title="Свой период">
            Укажите даты и нажмите «Показать», чтобы загрузить данные.
          </Alert>
        </Stack>
      </PageShell>
    )
  }

  if (customRangeError) {
    return (
      <PageShell>
        <Stack gap="md">
          {periodFilters}
          <Alert color="yellow" title="Период">
            {customRangeError}
          </Alert>
        </Stack>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        {periodFilters}

        <Tabs value={tab} onChange={(v) => v && setTab(v)} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="overview">Общий обзор</Tabs.Tab>
            <Tabs.Tab value="reviews">Отзывы</Tabs.Tab>
            <Tabs.Tab value="operators">Операторы</Tabs.Tab>
            <Tabs.Tab value="tags">Метрики по меткам</Tabs.Tab>
            <Tabs.Tab value="ai-ops">Мозгобот</Tabs.Tab>
            {canDebug ? <Tabs.Tab value="slow">Зависшие обращения</Tabs.Tab> : null}
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Suspense fallback={<Alert color="gray">Загрузка обзора…</Alert>}>
              {tab === 'overview' ? (
                <AnalyticsOverviewTab
                  key={`${refreshKey}:${periodKey}`}
                  period={period}
                  periodParams={periodParams}
                  projects={projects}
                  projectId={selectedProjectId}
                />
              ) : null}
            </Suspense>
          </Tabs.Panel>
          <Tabs.Panel value="reviews" pt="md">
            <Suspense fallback={<Alert color="gray">Загрузка отзывов…</Alert>}>
              {tab === 'reviews' ? (
                <AnalyticsReviewsTab
                  key={`${refreshKey}:${periodKey}`}
                  period={period}
                  periodParams={periodParams}
                  projectId={selectedProjectId}
                  projects={projects}
                />
              ) : null}
            </Suspense>
          </Tabs.Panel>
          <Tabs.Panel value="operators" pt="md">
            <Suspense fallback={<Alert color="gray">Загрузка операторов…</Alert>}>
              {tab === 'operators' ? (
                <AnalyticsOperatorsTab
                  key={`${refreshKey}:${periodKey}`}
                  period={period}
                  periodParams={periodParams}
                  projectId={selectedProjectId}
                  projects={projects}
                />
              ) : null}
            </Suspense>
          </Tabs.Panel>
          <Tabs.Panel value="tags" pt="md">
            <Suspense fallback={<Alert color="gray">Загрузка метрик…</Alert>}>
              {tab === 'tags' ? (
                <AnalyticsTagsTab key={`${refreshKey}:${periodKey}`} periodParams={periodParams} projects={projects} />
              ) : null}
            </Suspense>
          </Tabs.Panel>
          <Tabs.Panel value="ai-ops" pt="md">
            <Suspense fallback={<Alert color="gray">Загрузка метрик Мозгобота…</Alert>}>
              {tab === 'ai-ops' ? <AnalyticsAiOpsTab key={`${refreshKey}:${periodKey}`} periodParams={periodParams} /> : null}
            </Suspense>
          </Tabs.Panel>
          {canDebug ? (
            <Tabs.Panel value="slow" pt="md">
              <Suspense fallback={<Alert color="gray">Загрузка зависших обращений…</Alert>}>
                {tab === 'slow' ? (
                  <AnalyticsSlowTicketsTab key={`${refreshKey}:${periodKey}`} periodParams={periodParams} projectId={selectedProjectId} />
                ) : null}
              </Suspense>
            </Tabs.Panel>
          ) : null}
        </Tabs>
      </Stack>
    </PageShell>
  )
}
